/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MatNativeDateModule, MatRippleModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { TextMaskModule } from 'angular2-text-mask';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { ToastrModule } from 'ngx-toastr';
import { StorageServiceModule } from 'ngx-webstorage-service';
import { SendingDocumentsComponent } from 'src/app/components/sending-documents/sending-documents.component';
import { FileDropDirective } from 'src/directives/file-drop.directive';
import { FileDirective } from 'src/directives/file.directive';
import { SortByPipe } from 'src/pipes/sort-by.pipe';
import { DeactivateGuardService } from 'src/services/deactivate-guard.service';
import { OnlyNumberDirective } from '../directives/only-number.directive';
import { environment } from './../environments/environment';
import { AccountingProceduresModule } from './accounting-procedures/accounting-procedures.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AssignCaseDialogComponent } from './assign-case-dialog/assign-case-dialog.component';
import { CasesComponent } from './cases/cases.component';
import { DutyProceedingsInitiationComponent } from './cases/duty-proceedings-initiation/duty-proceedings-initiation.component';
import { CaseListItemComponent } from './components/case-list-item/case-list-item.component';
import { CollapsibleGroupComponent } from './components/collapsible-group/collapsible-group.component';
import { ComplainDecisionComponent } from './components/complain-decision/complain-decision.component';
import { ConnectDisconnectCpsuFosterChildComponent } from './components/connect-disconnect-cpsu-foster-child/connect-disconnect-cpsu-foster-child.component';
import { DefinitionItemDividerComponent } from './components/definition-list/definition-item-divider/definition-item-divider.component';
import { DossierStatusLabelComponent } from './components/dossier-status-label/dossier-status-label.component';
import { FamilyMemberCardComponent } from './components/family-member-card/family-member-card.component';
import { IconFileTrackingComponent } from './components/icons/icon-file-tracking/icon-file-tracking.component';
import { InputParticipationSkComponent } from './components/input-participation-sk/input-participation-sk.component';
import { InputSubjectInCpsuComponent } from './components/input-subject-in-cpsu/input-subject-in-cpsu.component';
import { InputSubjectInServiceProviderComponent } from './components/input-subject-in-service-provider/input-subject-in-service-provider.component';
import { InputSubjectInSkFormComponent } from './components/input-subject-in-sk-form/input-subject-in-sk-form.component';
import { NotificationsDropdownComponent } from './components/notifications-dropdown/notifications-dropdown.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { PrintComponent } from './components/print/print.component';
import { AddJmbgComponent } from './components/profile-card/add-jmbg/add-jmbg.component';
import { ChangePasswordComponent } from './components/profile-card/change-password/change-password.component';
import { ProfileCardComponent } from './components/profile-card/profile-card.component';
import { ProfileDropdownComponent } from './components/profile-dropdown/profile-dropdown.component';
import { TrackingListItemComponent } from './components/tracking-list-item/tracking-list-item.component';
import { TreeChecklistComponent } from './components/tree-checklist/tree-checklist.component';
import { WarningSessionComponent } from './components/warning-session/warning-session.component';
import { EntranceComponent } from './entrance/entrance.component';
import { FamilyMembersComponent } from './entrance/family-members/family-members.component';
import { ViolenceMembersComponent } from './entrance/violence-members/violence-members.component';
import { ForwardCaseDialogComponent } from './forward-case-dialog/forward-case-dialog.component';
import { CaseSearchByClassificationComponent } from './global-admin/case-search-by-classification/case-search-by-classification.component';
import { HomepageComponent } from './homepage/homepage.component';
import { LegalSubjectDetailsComponent } from './legal-subjects/legal-subject-details/legal-subject-details.component';
import { LegalSubjectHistoryComponent } from './legal-subjects/legal-subject-details/legal-subject-history/legal-subject-history.component';
import { LegalSubjectFormComponent } from './legal-subjects/legal-subject-form/legal-subject-form.component';
import { LegalSubjectsComponent } from './legal-subjects/legal-subjects.component';
import { NewLegalSubjectComponent } from './legal-subjects/new-legal-subject/new-legal-subject.component';
import { LoginComponent } from './login/login.component';
import { NotificationContentComponent } from './notification-content/notification-content.component';
import { NspFamilyRequestComponent } from './nsp-request/nsp-family-request/nsp-family-request.component';
import { NspRequestComponent } from './nsp-request/nsp-request.component';
import { ProfileComponent } from './profile/profile.component';
import { ReturnCaseDialogComponent } from './return-case-dialog/return-case-dialog.component';
import { ReturnCaseReasonDialogComponent } from './return-case-reason-dialog/return-case-reason-dialog.component';
import { RightsHistoryDialogComponent } from './rights-history-dialog/rights-history-dialog.component';
import { CollapsibleCaseComponent } from './schedule-cases/collapsible-case/collapsible-case.component';
import { CollapsibleWorkerComponent } from './schedule-cases/collapsible-worker/collapsible-worker.component';
import { CollapsibleWorkersGroupsComponent } from './schedule-cases/collapsible-workers-groups/collapsible-workers-groups.component';
import { ScheduleCasesComponent } from './schedule-cases/schedule-cases.component';
import { SendIntoPlannerComponent } from './send-into-planner/send-into-planner.component';
import { ContactPersonDialogComponent } from './service-providers/contact-person-dialog/contact-person-dialog.component';
import { SharedModule } from './shared/shared.module';
import { DeleteTrackingComponent } from './sidebar-widget/delete-tracking/delete-tracking.component';
import { ManageTrackingComponent } from './sidebar-widget/manage-tracking/manage-tracking.component';
import { OtherInstitutionsComponent } from './sidebar-widget/other-institutions/other-institutions.component';
import { PreviewDocumentComponent } from './sidebar-widget/preview-document/preview-document.component';
import { ServiceProviderManageTrackingComponent } from './sidebar-widget/service-provider-manage-tracking/service-provider-manage-tracking.component';
import { ServiceProviderSubjectDocumentsComponent } from './sidebar-widget/service-provider-subject-documents/service-provider-subject-documents.component';
import { ServiceProviderSubjectTrackingComponent } from './sidebar-widget/service-provider-subject-tracking/service-provider-subject-tracking.component';
import { SideSubjectCasesComponent } from './sidebar-widget/side-subject-cases/side-subject-cases.component';
import { SideSubjectsComponent } from './sidebar-widget/side-subjects/side-subjects.component';
import { SidebarWidgetComponent } from './sidebar-widget/sidebar-widget.component';
import { SubjectDocumentsDialogComponent } from './sidebar-widget/subject-documents/subject-documents-dialog/subject-documents-dialog.component';
import { SubjectDocumentsComponent } from './sidebar-widget/subject-documents/subject-documents.component';
import { SubjectTrackingComponent } from './sidebar-widget/subject-tracking/subject-tracking.component';
import { AssigneDossierComponent } from './subjects/assigne-dossier/assigne-dossier.component';
import { CpsuRegistryComponent } from './subjects/cpsu-registry/cpsu-registry.component';
import { CpsuSubjectDetailsComponent } from './subjects/cpsu-subject-details/cpsu-subject-details.component';
import { CpsuSubjectHistoryComponent } from './subjects/cpsu-subject-details/cpsu-subject-history/cpsu-subject-history.component';
import { CpsuSubjectPersonalInfoComponent } from './subjects/cpsu-subject-details/cpsu-subject-personal-info/cpsu-subject-personal-info.component';
import { FosterFamilyMemberComponent } from './subjects/cpsu-subject-details/foster-family/foster-family-member/foster-family-member.component';
import { FosterFamilyComponent } from './subjects/cpsu-subject-details/foster-family/foster-family.component';
import { DeleteDialogComponent } from './subjects/delete-dialog/delete-dialog.component';
import { ImportSubjectFromProviderComponent } from './subjects/import-subject-from-provider/import-subject-from-provider.component';
import { NewSubjectComponent } from './subjects/new-subject/new-subject.component';
import { RegistryComponent } from './subjects/registry/registry.component';
import { ServiceProviderAssessmentContactPersonDialogComponent } from './subjects/service-provider-subject-details/service-provider-assessment-contact-person-dialog/service-provider-assessment-contact-person-dialog.component';
import { ServiceProviderAssessmentDialogComponent } from './subjects/service-provider-subject-details/service-provider-assessment-dialog/service-provider-assessment-dialog.component';
import { EfficiencyRowFormComponent } from './subjects/service-provider-subject-details/service-provider-functional-efficiency-assessment-scale-dialog/efficiency-row-form/efficiency-row-form.component';
import { ServiceProviderFunctionalEfficiencyAssessmentScaleDialogComponent } from './subjects/service-provider-subject-details/service-provider-functional-efficiency-assessment-scale-dialog/service-provider-functional-efficiency-assessment-scale-dialog.component';
import { ServiceProviderIndividualServicePlanActivityDialogComponent } from './subjects/service-provider-subject-details/service-provider-individual-service-plan-activity-dialog/service-provider-individual-service-plan-activity-dialog.component';
import { ServiceProviderIndividualServicePlanDialogComponent } from './subjects/service-provider-subject-details/service-provider-individual-service-plan-dialog/service-provider-individual-service-plan-dialog.component';
import { ServiceProviderIntakeAssessmentDialogComponent } from './subjects/service-provider-subject-details/service-provider-intake-assessment-dialog/service-provider-intake-assessment-dialog.component';
import { ServiceProviderSubjectDetailsComponent } from './subjects/service-provider-subject-details/service-provider-subject-details.component';
import { ServiceProviderSubjectHistoryComponent } from './subjects/service-provider-subject-details/service-provider-subject-history/service-provider-subject-history.component';
import { ServiceProviderSubjectNewRecordComponent } from './subjects/service-provider-subject-details/service-provider-subject-new-record/service-provider-subject-new-record.component';
import { ServiceProviderSubjectPersonalInfoComponent } from './subjects/service-provider-subject-details/service-provider-subject-personal-info/service-provider-subject-personal-info.component';
import { ServiceProviderSubjectRecordComponent } from './subjects/service-provider-subject-details/service-provider-subject-record/service-provider-subject-record.component';
import { ServiceProviderSubjectFormComponent } from './subjects/service-provider-subject-form/service-provider-subject-form.component';
import { ServiceProvidersRegistryComponent } from './subjects/service-providers-registry/service-providers-registry.component';
import { ArchiveSubjectComponent } from './subjects/subject-details/archive-subject/archive-subject.component';
import { SubjectCaseComponent } from './subjects/subject-details/subject-case/subject-case.component';
import { SubjectCasesComponent } from './subjects/subject-details/subject-cases/subject-cases.component';
import { SubjectDetailsComponent } from './subjects/subject-details/subject-details.component';
import { FamilyDossierComponent } from './subjects/subject-details/subject-family/family-dossier/family-dossier.component';
import { SubjectFamilyComponent } from './subjects/subject-details/subject-family/subject-family.component';
import { SubjectHistoryComponent } from './subjects/subject-details/subject-history/subject-history.component';
import { SubjectNewProblemComponent } from './subjects/subject-details/subject-new-problem/subject-new-problem.component';
import { SubjectNewRecordComponent } from './subjects/subject-details/subject-new-record/subject-new-record.component';
import { SubjectProblemsComponent } from './subjects/subject-details/subject-problems/subject-problems.component';
import { SubjectRecordComponent } from './subjects/subject-details/subject-record/subject-record.component';
import { SubjectFormComponent } from './subjects/subject-form/subject-form.component';
import { SubjectsComponent } from './subjects/subjects.component';
import { SubmissionItemComponent } from './submissions/submission-item/submission-item.component';
import { SubmissionsComponent } from './submissions/submissions.component';
import { CompetencyQuestionnaireNewComponent } from './supervision/competency-questionnaire-new/competency-questionnaire-new.component';
import { GoalsFormComponent } from './supervision/competency-questionnaire-new/goals-form/goals-form.component';
import { RowFormComponent } from './supervision/competency-questionnaire-new/row-form/row-form.component';
import { TeamRequestComponent } from './team-request/team-request.component';
import { PageComponent } from './templates/page/page.component';
import { TokenInterceptor } from './token.interceptor';
import { AppDateAdapter } from './utils/AppDateAdapter';
import { WarningMessageComponent } from './warning-message/warning-message.component';
import { WorkloadCalculationDialogComponent } from './workload-calculation-dialog/workload-calculation-dialog.component';
import { SendDocumentToCsrModalComponent } from './components/send-document-to-csr-modal/send-document-to-csr-modal.component';
import { DocumentsForSendingComponent } from './components/send-document-to-csr-modal/documents-for-sending/documents-for-sending.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
   return new TranslateHttpLoader(http, environment.translateUrl);
}

@NgModule({
   exports: [],
   declarations: [
      AppComponent,
      SubjectsComponent,
      DeleteDialogComponent,
      LoginComponent,
      AssignCaseDialogComponent,
      EntranceComponent,
      SubjectHistoryComponent,
      SubjectCaseComponent,
      SortByPipe,
      ProfileComponent,
      PageComponent,
      SubjectDetailsComponent,
      NewSubjectComponent,
      ArchiveSubjectComponent,
      DefinitionItemDividerComponent,
      SubjectFormComponent,
      FamilyMembersComponent,
      SubjectCasesComponent,
      CaseListItemComponent,
      SubjectProblemsComponent,
      SubjectRecordComponent,
      SubjectFamilyComponent,
      FamilyMemberCardComponent,
      CollapsibleGroupComponent,
      PageNotFoundComponent,
      NotificationContentComponent,
      DossierStatusLabelComponent,
      HomepageComponent,
      SidebarWidgetComponent,
      IconFileTrackingComponent,
      WarningMessageComponent,
      PreviewDocumentComponent,
      TrackingListItemComponent,
      SubjectTrackingComponent,
      ManageTrackingComponent,
      DeleteTrackingComponent,
      CollapsibleGroupComponent,
      SubjectNewProblemComponent,
      SubjectNewRecordComponent,
      NspRequestComponent,
      NspFamilyRequestComponent,
      SideSubjectsComponent,
      OtherInstitutionsComponent,
      SendingDocumentsComponent,
      TreeChecklistComponent,
      LegalSubjectsComponent,
      NewLegalSubjectComponent,
      LegalSubjectFormComponent,
      LegalSubjectDetailsComponent,
      LegalSubjectHistoryComponent,
      PrintComponent,
      ScheduleCasesComponent,
      CollapsibleWorkerComponent,
      CollapsibleCaseComponent,
      SubmissionsComponent,
      SubmissionItemComponent,
      TeamRequestComponent,
      FamilyDossierComponent,
      SendIntoPlannerComponent,
      AssigneDossierComponent,
      CollapsibleWorkersGroupsComponent,
      NotificationsDropdownComponent,
      ProfileCardComponent,
      ProfileDropdownComponent,
      OnlyNumberDirective,
      SubjectDocumentsDialogComponent,
      CasesComponent,
      ViolenceMembersComponent,
      ImportSubjectFromProviderComponent,
      FileDropDirective,
      FileDirective,
      ChangePasswordComponent,
      ForwardCaseDialogComponent,
      ReturnCaseDialogComponent,
      WorkloadCalculationDialogComponent,
      ComplainDecisionComponent,
      AddJmbgComponent,
      InputSubjectInSkFormComponent,
      InputParticipationSkComponent,
      WarningSessionComponent,
      SideSubjectCasesComponent,
      ReturnCaseReasonDialogComponent,
      CaseSearchByClassificationComponent,
      RightsHistoryDialogComponent,
      CpsuSubjectDetailsComponent,
      CpsuSubjectPersonalInfoComponent,
      CpsuSubjectHistoryComponent,
      RegistryComponent,
      CpsuRegistryComponent,
      InputSubjectInCpsuComponent,
      ContactPersonDialogComponent,
      ServiceProvidersRegistryComponent,
      InputSubjectInServiceProviderComponent,
      ServiceProviderSubjectDetailsComponent,
      ServiceProviderSubjectPersonalInfoComponent,
      ServiceProviderSubjectHistoryComponent,
      ServiceProviderSubjectDocumentsComponent,
      ServiceProviderSubjectTrackingComponent,
      ServiceProviderManageTrackingComponent,
      ServiceProviderSubjectFormComponent,
      ServiceProviderSubjectRecordComponent,
      ServiceProviderSubjectNewRecordComponent,
      ServiceProviderAssessmentDialogComponent,
      ServiceProviderIntakeAssessmentDialogComponent,
      ServiceProviderAssessmentContactPersonDialogComponent,
      ServiceProviderIndividualServicePlanDialogComponent,
      ServiceProviderIndividualServicePlanActivityDialogComponent,
      FosterFamilyComponent,
      FosterFamilyMemberComponent,
      ConnectDisconnectCpsuFosterChildComponent,
      CompetencyQuestionnaireNewComponent,
      RowFormComponent,
      GoalsFormComponent,
      ServiceProviderFunctionalEfficiencyAssessmentScaleDialogComponent,
      EfficiencyRowFormComponent,
      DutyProceedingsInitiationComponent,
      SendDocumentToCsrModalComponent,
      DocumentsForSendingComponent,
   ],
   imports: [
      BrowserModule,
      PdfViewerModule,
      BrowserAnimationsModule,
      FormsModule,
      MatAutocompleteModule,
      ReactiveFormsModule,
      MatCheckboxModule,
      MatButtonModule,
      MatFormFieldModule,
      MatInputModule,
      MatTableModule,
      MatSortModule,
      MatDialogModule,
      MatSelectModule,
      MatChipsModule,
      MatPaginatorModule,
      MatExpansionModule,
      MatGridListModule,
      MatProgressSpinnerModule,
      HttpClientModule,
      MatMenuModule,
      MatSidenavModule,
      MatListModule,
      MatToolbarModule,
      MatIconModule,
      MatDatepickerModule,
      MatRippleModule,
      MatTooltipModule,
      MatNativeDateModule,
      MatRadioModule,
      MatCardModule,
      MatAutocompleteModule,
      MatStepperModule,
      MatBadgeModule,
      MatTreeModule,
      StorageServiceModule,
      MatSlideToggleModule,
      NgxDocViewerModule,
      KeyboardShortcutsModule.forRoot(),
      ToastrModule.forRoot(),
      TranslateModule.forRoot({
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
         },
      }),
      AppRoutingModule,
      TextMaskModule,
      AutocompleteLibModule,
      ServiceWorkerModule.register('./ngsw-worker.js', { enabled: environment.production, registrationStrategy: 'registerImmediately' }),
      ScrollingModule,
      SharedModule,
      AccountingProceduresModule,
   ],
   providers: [
      Title,
      {
         provide: HTTP_INTERCEPTORS,
         useClass: TokenInterceptor,
         multi: true,
      },
      DatePipe,
      {
         provide: SwRegistrationOptions,
         useFactory: () => ({ enabled: environment.production }),
      },
      { provide: DateAdapter, useClass: AppDateAdapter },
      { provide: MAT_DATE_LOCALE, useValue: 'sr-Cyrl' },
      DeactivateGuardService,
   ],
   entryComponents: [
      WorkloadCalculationDialogComponent,
      DeleteDialogComponent,
      ChangePasswordComponent,
      AssignCaseDialogComponent,
      ForwardCaseDialogComponent,
      ReturnCaseDialogComponent,
      SubjectHistoryComponent,
      SubjectCaseComponent,
      SubjectDocumentsComponent,
      ArchiveSubjectComponent,
      EntranceComponent,
      FamilyMembersComponent,
      PreviewDocumentComponent,
      ManageTrackingComponent,
      DeleteTrackingComponent,
      SubjectNewProblemComponent,
      SubjectNewRecordComponent,
      NspFamilyRequestComponent,
      SendingDocumentsComponent,
      PrintComponent,
      WarningMessageComponent,
      TeamRequestComponent,
      FamilyDossierComponent,
      SendIntoPlannerComponent,
      SendIntoPlannerComponent,
      AssigneDossierComponent,
      SubjectDocumentsDialogComponent,
      ViolenceMembersComponent,
      ImportSubjectFromProviderComponent,
      ComplainDecisionComponent,
      AddJmbgComponent,
      InputSubjectInSkFormComponent,
      InputParticipationSkComponent,
      ReturnCaseReasonDialogComponent,
      RightsHistoryDialogComponent,
      ContactPersonDialogComponent,
      InputSubjectInCpsuComponent,
      InputSubjectInServiceProviderComponent,
      InputSubjectInCpsuComponent,
      ServiceProviderManageTrackingComponent,
      ServiceProviderSubjectNewRecordComponent,
      ServiceProviderAssessmentDialogComponent,
      ServiceProviderIntakeAssessmentDialogComponent,
      ServiceProviderAssessmentContactPersonDialogComponent,
      ServiceProviderIndividualServicePlanDialogComponent,
      ServiceProviderIndividualServicePlanActivityDialogComponent,
      ConnectDisconnectCpsuFosterChildComponent,
      CompetencyQuestionnaireNewComponent,
      GoalsFormComponent,
      ServiceProviderIndividualServicePlanActivityDialogComponent,
      ServiceProviderFunctionalEfficiencyAssessmentScaleDialogComponent,
      SendDocumentToCsrModalComponent,
      DocumentsForSendingComponent,
   ],
   bootstrap: [AppComponent],
})
export class AppModule {
   constructor() {
      //   dateAdapter.setLocale('sr-Cyrl');
   }
}
